import React from 'react';
import { WebsiteDesignProps } from './types';
import { MousePointer2, ArrowUpRight, Circle, Square, Triangle } from 'lucide-react';
import { Button } from "@/components/ui/button";

const BauhausModern: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Manifesto Design School";

    return (
        <div className="bg-[#f0f0f0] text-black min-h-screen font-sans">
            <div className="grid grid-cols-12 min-h-screen">
                {/* Left Sidebar Nav */}
                <div className="col-span-12 md:col-span-2 bg-white border-r-4 border-black p-6 flex flex-col justify-between sticky top-0 md:h-screen z-20">
                    <div>
                        <div className="w-16 h-16 bg-blue-600 rounded-full mb-8 border-4 border-black flex items-center justify-center text-white font-bold text-2xl">M</div>
                        <h1 className="text-3xl font-black uppercase leading-none mb-12 transform -rotate-1 origin-top-left">{schoolName}</h1>
                        
                        <nav className="flex flex-col gap-4 font-bold text-xl uppercase">
                            <a href="#" className="hover:bg-yellow-400 -ml-2 px-2 py-1 transition-colors">Manifesto</a>
                            <a href="#" className="hover:bg-red-500 hover:text-white -ml-2 px-2 py-1 transition-colors">Courses</a>
                            <a href="#" className="hover:bg-blue-600 hover:text-white -ml-2 px-2 py-1 transition-colors">Gallery</a>
                            <a href="#" className="hover:bg-black hover:text-white -ml-2 px-2 py-1 transition-colors">Contact</a>
                        </nav>
                    </div>
                    <div className="hidden md:block">
                        <div className="w-full aspect-square bg-red-500 border-4 border-black mb-4"></div>
                        <div className="text-xs font-mono">EST. 1919</div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="col-span-12 md:col-span-10">
                    {/* Hero Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 min-h-[60vh] border-b-4 border-black">
                        <div className="bg-yellow-400 p-12 flex flex-col justify-center border-b-4 md:border-b-0 md:border-r-4 border-black relative overflow-hidden group">
                             <div className="absolute top-[-50px] right-[-50px] w-48 h-48 bg-white border-4 border-black rounded-full z-0 group-hover:scale-125 transition-transform duration-500"></div>
                             <h2 className="text-6xl md:text-8xl font-black uppercase leading-[0.8] relative z-10 mix-blend-multiply">
                                 Form <br/> Follows <br/> Function
                             </h2>
                        </div>
                        <div className="relative overflow-hidden grayscale hover:grayscale-0 transition-all duration-500 bg-white">
                             <img src="https://images.unsplash.com/photo-1561557944-6e7860d1a7eb?q=80&w=1974&auto=format&fit=crop" className="w-full h-full object-cover" alt="Architecture" />
                             <div className="absolute inset-0 bg-blue-600/20 mix-blend-multiply opacity-0 hover:opacity-100 transition-opacity"></div>
                             <div className="absolute bottom-0 left-0 bg-black text-white p-4 font-mono text-sm border-t-4 border-r-4 border-black">
                                 ARCHITECTURAL_WORKSHOP_01
                             </div>
                        </div>
                    </div>

                    {/* Content Blocks */}
                    <div className="grid grid-cols-1 md:grid-cols-3 border-b-4 border-black min-h-[40vh]">
                        <div className="p-8 md:border-r-4 border-black bg-white flex flex-col justify-between hover:bg-red-50 transition-colors">
                            <Square size={48} className="mb-4 text-red-600 fill-current" />
                            <div>
                                <h3 className="text-2xl font-bold uppercase mb-4">Structure</h3>
                                <p className="font-mono text-sm leading-relaxed">
                                    We strip away the unnecessary. Education is built on solid foundations, logical progression, and clarity of purpose.
                                </p>
                            </div>
                        </div>
                        <div className="p-8 md:border-r-4 border-black bg-white flex flex-col justify-between hover:bg-blue-50 transition-colors">
                            <Circle size={48} className="mb-4 text-blue-600 fill-current" />
                            <div>
                                <h3 className="text-2xl font-bold uppercase mb-4">Unity</h3>
                                <p className="font-mono text-sm leading-relaxed">
                                    Art and technology are not separate. We combine the craftsman's skill with the artist's vision.
                                </p>
                            </div>
                        </div>
                        <div className="p-8 bg-white flex flex-col justify-between hover:bg-yellow-50 transition-colors">
                            <Triangle size={48} className="mb-4 text-yellow-400 fill-current" />
                            <div>
                                <h3 className="text-2xl font-bold uppercase mb-4">Expression</h3>
                                <p className="font-mono text-sm leading-relaxed">
                                    Boldness is encouraged. Fear is rejected. Create something that has never existed before.
                                </p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Footer Strip */}
                    <div className="bg-black text-white p-6 flex justify-between items-center font-mono">
                         <div className="flex gap-4">
                             <span>&copy; 2026 {schoolName}</span>
                         </div>
                         <Button className="bg-white text-black hover:bg-blue-600 hover:text-white border-0 font-bold uppercase rounded-none transition-colors">
                             Start Application <ArrowUpRight className="ml-2 w-4 h-4" />
                         </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BauhausModern;
